import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Globe, AlertTriangle, Search, RefreshCw, Briefcase, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { useLoading } from '../../hooks/useLoading';
import { sleep } from '../../utils/sleep';

const BrandDashboard = () => {
  const { isLoading, withLoading } = useLoading();

  const handleRefresh = async () => {
    await withLoading(async () => {
      await sleep(3000);
      console.log('Refreshing brand protection data...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Refreshing data..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Briefcase className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Brand Protection</h1>
            <p className="text-sm text-gray-400">Monitor and protect your brand from impersonation and abuse</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={handleRefresh}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-4 gap-6">
        {[
          { label: 'Brand Mentions', value: '1,245', trend: '+23', icon: Globe, color: 'text-blue-500' },
          { label: 'Domain Squatting', value: '12', trend: '+3', icon: AlertTriangle, color: 'text-red-500' },
          { label: 'Trademark Violations', value: '8', trend: '+2', icon: Shield, color: 'text-purple-500' },
          { label: 'Protected Assets', value: '156', trend: '+5', icon: Briefcase, color: 'text-emerald-500' }
        ].map((stat) => (
          <div key={stat.label} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">{stat.label}</span>
              <stat.icon className={`h-5 w-5 ${stat.color}`} />
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className={`text-xs ${stat.trend.startsWith('+') ? 'text-red-500' : 'text-emerald-500'}`}>
                {stat.trend}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Brand Monitoring */}
      <div className="grid grid-cols-2 gap-6">
        {/* Recent Violations */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Violations</h2>
          <div className="space-y-4">
            {[
              { type: 'Domain', title: 'Typosquatting Detected', severity: 'high', time: '2h ago' },
              { type: 'Trademark', title: 'Logo Misuse', severity: 'medium', time: '4h ago' },
              { type: 'Social', title: 'Brand Impersonation', severity: 'critical', time: '6h ago' }
            ].map((violation, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      violation.severity === 'critical' ? 'bg-red-500/10 text-red-500' :
                      violation.severity === 'high' ? 'bg-orange-500/10 text-orange-500' :
                      'bg-yellow-500/10 text-yellow-500'
                    }`}>
                      {violation.severity}
                    </span>
                    <span className="text-sm font-medium text-white">{violation.title}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Type: {violation.type}</div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-500">{violation.time}</span>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/brand-protection/violations/${index + 1}`}>View</Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Brand Sentiment */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Brand Sentiment</h2>
          <div className="space-y-4">
            {[
              { platform: 'Social Media', sentiment: 'Positive', score: 85, trend: 'up' },
              { platform: 'News Articles', sentiment: 'Neutral', score: 65, trend: 'stable' },
              { platform: 'Forums', sentiment: 'Mixed', score: 55, trend: 'down' }
            ].map((item, index) => (
              <div key={index} className="p-4 bg-gray-800/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-white">{item.platform}</div>
                  <div className={`text-xs ${
                    item.trend === 'up' ? 'text-emerald-500' :
                    item.trend === 'down' ? 'text-red-500' :
                    'text-gray-500'
                  }`}>
                    {item.trend === 'up' ? '↑' : item.trend === 'down' ? '↓' : '→'}
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          item.score >= 80 ? 'bg-emerald-500' :
                          item.score >= 60 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${item.score}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-white">{item.score}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Protected Assets */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Protected Brand Assets</h2>
          <Button disabled>Add Asset</Button>
        </div>
        <div className="grid grid-cols-3 gap-6">
          {[
            { type: 'Domain', count: 24, protected: 22, risk: 'low' },
            { type: 'Trademark', count: 12, protected: 12, risk: 'none' },
            { type: 'Social Media', count: 8, protected: 6, risk: 'medium' }
          ].map((asset, index) => (
            <div key={index} className="p-4 bg-gray-800/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-white">{asset.type}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  asset.risk === 'none' ? 'bg-emerald-500/10 text-emerald-500' :
                  asset.risk === 'low' ? 'bg-blue-500/10 text-blue-500' :
                  'bg-yellow-500/10 text-yellow-500'
                }`}>
                  {asset.risk === 'none' ? 'Protected' : `${asset.risk} risk`}
                </span>
              </div>
              <div className="text-2xl font-bold text-white mb-2">
                {asset.protected}/{asset.count}
              </div>
              <div className="text-xs text-gray-400">Protected assets</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BrandDashboard;